import './coapplicantEmploymentDurationPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/shared_new/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/halfYear';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeMonthsAt,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeMonthsAt,
    syncCoapplicant,
  } = useCoapplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      monthsAtIncomeValue: coapplicantCurrentIncomeMonthsAt,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const monthsAtIncomeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentIncomeMonthsAt) && isValidClickEvent(event),
    );

    setCoapplicantCurrentIncomeMonthsAt(monthsAtIncomeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { monthsAtIncomeValue } = values;

    if (!monthsAtIncomeValue) {
      return;
    }

    if (isCoapplicantPersisted) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-employment-duration-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantEmploymentDuration.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEmploymentDuration.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEmploymentDuration.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantEmploymentDuration.progress}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEmploymentDuration.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantEmploymentDuration.form.h1
                }
              >
                {t('CoapplicantEmploymentDurationPage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantEmploymentDuration.form.h2
                }
              >
                {t('CoapplicantEmploymentDurationPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEmploymentDuration.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={coapplicantCurrentIncomeMonthsAt}
                  labelPlacement="below"
                  name="monthsAtIncomeValue"
                  options={[
                    {
                      label: t('CoapplicantEmploymentDurationPage.yes'),
                      value: ONE_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.coapplicantEmploymentDuration.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantEmploymentDurationPage.no'),
                      value: HALF_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.coapplicantEmploymentDuration.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantEmploymentDuration.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantEmploymentDuration.form
                      .submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentIncomeMonthsAt)}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantEmploymentDurationPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantEmploymentDuration.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEmploymentDurationPage';
